import { useEndpoint } from 'api';
import {
	LoadingSkeleton,
	LoadingSkeletonItem,
} from 'components/LoadingSkeleton';
import Message from 'components/Message';
import { useSafeFieldValues } from 'hooks';
import type {
	Field,
	PartialPopoverProps,
	SitecoreBlockProps,
	SitecoreLink,
} from 'models/sitecore';
import type { HTMLString } from 'types';
import { useI18n } from 'utils/i18n';

import CreditSimulation from './CreditSimulation';

export interface CreditSimulationPartialProps {
	price: number;
}
interface Props extends SitecoreBlockProps {
	fields?: {
		applicationLink: Field<SitecoreLink>;
		content: Field<HTMLString>;
		example: Field<string>;
		heading: Field<string>;
		interestRateText?: Field<string>;
		invoiceFeeText?: Field<string>;
		monthlyCostText?: Field<string>;
		repaymentTimeText?: Field<string>;
		sekkiLink: Field<SitecoreLink>;
		startupFeeText?: Field<string>;
		tableHeading: Field<HTMLString>;
		termsLink: Field<SitecoreLink>;
		totalAmountText?: Field<string>;
	};
	partialProps?: PartialPopoverProps<CreditSimulationPartialProps>;
}

export default function CreditSimulationBlock({ fields, partialProps }: Props) {
	const texts = useSafeFieldValues(fields);
	const { t } = useI18n();

	const { data, error, isLoading } = useEndpoint(
		partialProps?.price
			? {
					name: 'GetCreditSimulation',
					queryParams: { price: partialProps?.price },
				}
			: null,
	);

	if (!fields) {
		return null;
	}

	return (
		<>
			{!error && !isLoading && data?.costCalculations && (
				<CreditSimulation
					amountToPay={partialProps?.price}
					applicationLink={fields.applicationLink.value}
					content={fields.content.value}
					costCalculations={data.costCalculations}
					example={fields.example.value}
					heading={fields.heading.value}
					interestRateText={texts.interestRateText}
					invoiceFeeText={texts.invoiceFeeText}
					monthlyCostText={texts.monthlyCostText}
					repaymentTimeText={texts.repaymentTimeText}
					sekkiLink={fields.sekkiLink.value}
					startupFeeText={texts.startupFeeText}
					tableHeading={fields.tableHeading.value}
					termsLink={fields.termsLink.value}
					totalAmountText={texts.totalAmountText}
				/>
			)}
			{!error && isLoading && (
				<LoadingSkeleton>
					<LoadingSkeletonItem height="5rem" className="mt-4" />
					<LoadingSkeletonItem height="10rem" className="mt-4" />
					<LoadingSkeletonItem height="20rem" className="mt-4" />
					<LoadingSkeletonItem height="3rem" className="mt-4" />
					<LoadingSkeletonItem height="6rem" className="mt-4" />
				</LoadingSkeleton>
			)}
			{error && (
				<Message variant="error" text={t('general_critical_error_text')} />
			)}
		</>
	);
}
CreditSimulationBlock.displayName = 'CreditSimulationBlock';
